import { _js } from '@ifixit/utils/localize';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { getEditor, getSerializedText } from 'Shared/prosemirror/tiptap/editor_cache';

type EditorMode = 'visual' | 'source';

/** The server-parsed wikitext content a dual-mode editor mounts. */
export type WikitextSource = {
   /** ProseMirror doc from the server's wikitext parser (object or JSON string). */
   json: unknown;
   /** Raw wikitext source, shown in source mode. */
   text: string;
   /** Initial mode: false means start in source mode (`?plaintext`). */
   useWysiwyg: boolean;
   /**
    * Whether the viewer (admin/moderator) may switch into raw wikitext
    * source mode. When false the source toggle is hidden.
    */
   canEditSource: boolean;
   /** Whether this content supports the WYSIWYG editor at all. Default true. */
   wysiwygCapable?: boolean;
};

export type WikitextEditor = {
   editorid: string;
   /** Fires on every doc modification so dirty gating re-renders. */
   onModified: (modified: boolean) => void;
   mode: EditorMode;
   /** Whether the source/visual toggle is offered (admin/mod, or a way back). */
   canToggleMode: boolean;
   isSwitchingMode: boolean;
   modeSwitchError: string | null;
   toggleMode: () => void;
   visualDoc: string;
   visualDocKey: number;
   sourceText: string;
   setSourceText: (text: string) => void;
   getContents: () => string;
   isDirty: () => boolean;
   /**
    * After a successful save: the submitted contents become the clean
    * state. If the contents changed while the save ran, the field stays
    * dirty — the newer text isn't on the server.
    */
   rebaseline: (savedContents: string) => void;
};

/**
 * Dual visual/source state for one wikitext field, shared by the wiki
 * editor's body and the guide editor's introduction/conclusion: Tiptap edits
 * the server-parsed doc, serialization to wikitext happens client-side, and
 * the source -> visual switch parses through `parseToJson` (the same
 * server parser a pageload uses). `?plaintext` starts in source mode and
 * never mounts Tiptap until asked, so an unparseable document can't crash
 * the page again.
 *
 * Pages that stay put after a save call rebaseline() to mark the submitted
 * contents clean without a remount; pages that redirect never need to.
 */
export function useWikitextEditor(
   field: WikitextSource,
   editorid: string,
   parseToJson: (contents: string) => Promise<unknown>
): WikitextEditor {
   const [mode, setMode] = useState<EditorMode>(field.useWysiwyg ? 'visual' : 'source');
   const [sourceText, setSourceText] = useState(field.text);
   const [visualDoc, setVisualDoc] = useState(() => docString(field.json));
   const [visualDocKey, setVisualDocKey] = useState(0);
   // Mode switches remount Tiptap, which resets its own change tracking, so
   // track edits and the last-saved text ourselves.
   const sourceEditedRef = useRef(false);
   const baselineTextRef = useRef(field.text);
   // Typing inside Tiptap doesn't touch React state, so the page's dirty
   // gating (the disabled Save button) would never recompute; OriginalState
   // reports modifications and this tick forces the re-render.
   const [, setModifiedTick] = useState(0);
   const onModified = () => setModifiedTick(tick => tick + 1);

   const parseToDoc = useMutation({
      mutationFn: parseToJson,
   });

   const switchToSource = () => {
      // Tiptap is lazy-loaded. Keep the source text intact if the toggle is
      // used before the editor has registered instead of serializing to ''.
      if (!getEditor(editorid)) {
         setMode('source');
         return;
      }
      const serialized = getSerializedText(editorid);
      if (serialized !== sourceText) {
         sourceEditedRef.current = true;
         setSourceText(serialized);
      }
      setMode('source');
   };

   const switchToVisual = () => {
      parseToDoc.mutate(sourceText, {
         onSuccess: json => {
            setVisualDoc(docString(json));
            setVisualDocKey(key => key + 1);
            setMode('visual');
         },
      });
   };

   const editSourceText = (text: string) => {
      if (text !== sourceText) {
         sourceEditedRef.current = true;
      }
      setSourceText(text);
   };

   const getContents = () => {
      if (mode === 'source' || !getEditor(editorid)) {
         return sourceText;
      }
      return getSerializedText(editorid);
   };

   const isDirty = () => {
      if (mode === 'source') {
         return sourceEditedRef.current || sourceText !== baselineTextRef.current;
      }
      // Serialize-and-compare would run per render; the OriginalState doc
      // comparison is cheap. Source-mode edits that happened before a
      // switch back are tracked by the ref.
      return sourceEditedRef.current || isVisualEditorDirty(editorid);
   };

   const rebaseline = (savedContents: string) => {
      baselineTextRef.current = savedContents;
      // Typing while the save request ran means the current contents are
      // newer than what reached the server — marking them clean would
      // silently drop that delta on the next navigation. Keep the dirty
      // tracking as-is and only move the saved baseline.
      if (getContents() !== savedContents) {
         return;
      }
      sourceEditedRef.current = false;
      if (mode === 'source') {
         return;
      }
      // Mark the live doc as OriginalState's saved copy in place. This used
      // to parse-and-remount the editor, but the editor cache kept serving
      // the replaced (still-dirty) instance to isDirty, so the page read
      // dirty forever after any visual-mode save.
      getEditor(editorid)?.commands?.updateSavedContent();
   };

   return {
      editorid,
      onModified,
      mode,
      // The content must support WYSIWYG at all, and only admins/mods may
      // switch into source mode. Someone who still landed in source (via
      // `?plaintext` or a crash fallback) keeps the toggle to get back to
      // the visual editor, but can't re-enter source.
      canToggleMode: (field.wysiwygCapable ?? true) && (field.canEditSource || mode === 'source'),
      isSwitchingMode: parseToDoc.isPending,
      modeSwitchError: parseToDoc.isError
         ? _js('Could not open the visual editor. Your text is unchanged.')
         : null,
      toggleMode: mode === 'visual' ? switchToSource : switchToVisual,
      visualDoc,
      visualDocKey,
      sourceText,
      setSourceText: editSourceText,
      getContents,
      isDirty,
      rebaseline,
   };
}

function docString(json: unknown): string {
   // The parser hands back the doc as a JSON string; older callers pass the
   // decoded object. TiptapEditor wants the string form.
   return typeof json === 'string' ? json : JSON.stringify(json ?? '');
}

/** Compare the live doc against OriginalState's copy. */
function isVisualEditorDirty(editorid: string): boolean {
   const handle = getEditor(editorid);
   if (!handle) {
      return false;
   }
   const currentDoc = handle.state?.doc;
   const originalDoc = handle.storage?.originalState?.savedContent;
   if (!currentDoc || !originalDoc) {
      return false;
   }
   return !currentDoc.eq(originalDoc);
}
